import type { IdentityType } from "@p0security/shared/types/assessment/data/index";
import { assertNeverError } from "@p0security/shared/types/index";
import { uniq } from "lodash-es";

import type { AwsAccesses } from "./accesses.ts";
import type { AwsActionsShortcutService } from "./types.ts";
import type { AwsPartition } from "./util.ts";

// We support services that are indexed by resource explorer
// See https://docs.aws.amazon.com/resource-explorer/latest/userguide/supported-resource-types.html
const RESOURCE_EXPLORER_SERVICES = [
  "access-analyzer",
  "acm", // Certificate Manager
  "acm-pca", // Private Certificate Authority
  "airflow",
  "amplify",
  "apigateway",
  "app-integrations",
  "appconfig",
  "appflow",
  "appmesh",
  "aps", // Managed Service for Prometheus
  "apprunner",
  "appstream",
  "appsync",
  "athena",
  "auditmanager",
  "backup",
  "batch",
  "ce", // Cost Explorer
  "cloudformation",
  "cloudfront",
  "cloudtrail",
  "cloudwatch",
  "codeartifact",
  "codebuild",
  "codecommit",
  "codeconnections",
  "codedeploy",
  "codeguru-profiler",
  "codeguru-reviewer",
  "codepipeline",
  "codestar-connections",
  "cognito-identity",
  "cognito-idp",
  "comprehend",
  "connect",
  "databrew",
  "dataexchange",
  "datasync",
  "detective",
  "dms", // Database Migration Service
  "dynamodb",
  "ec2",
  "ecr", //  Elastic Container Registry
  "ecr-public",
  "ecs", // Elastic Container Service
  "eks",
  "elasticache",
  "elasticbeanstalk",
  "elasticfilesystem",
  "elasticloadbalancing",
  "elasticmapreduce",
  "emr-containers",
  "emr-serverless",
  "es", // Amazon OpenSearch Service
  "events",
  "evidently",
  "finspace",
  "firehose",
  "fis", // Fault Injection Service
  "forecast",
  "frauddetector",
  "fsx",
  "gamelift",
  "geo",
  "glacier",
  "globalaccelerator",
  "glue",
  "greengrass",
  "groundstation",
  "guardduty",
  "healthlake",
  "iam",
  "imagebuilder",
  "inspector",
  "iot",
  "iotanalytics",
  "iotdeviceadvisor",
  "iotevents",
  "iotfleetwise",
  "iotsitewise",
  "iottwinmaker",
  "iotwireless",
  "ivs", // Interactive Video Service
  "kafka",
  "kendra",
  "kinesis",
  "kinesisanalytics",
  "kinesisvideo",
  "kms",
  "lambda",
  "lex",
  "logs",
  "lookoutmetrics",
  "lookoutvision",
  "managedblockchain",
  "mediapackage",
  "mediapackage-vod",
  "mediatailor",
  "memorydb",
  "mobiletargeting",
  "mq",
  "network-firewall",
  "networkmanager",
  "oam",
  "omics",
  "panorama",
  "personalize",
  "pipes",
  "proton",
  "qldb", // Quantum Ledger Database
  "quicksight",
  "ram", // Resource Access Manager
  "rds",
  "rds-db", // RDS Database connections
  "redshift",
  "refactor-spaces",
  "rekognition",
  "resiliencehub",
  "resource-explorer-2",
  "resource-groups",
  "route53",
  "route53-recovery-readiness",
  "route53resolver",
  "sagemaker",
  "s3",
  "scheduler",
  "schemas",
  "secretsmanager",
  "servicecatalog",
  "ses", // Simple Email Service
  "signer",
  "sns", // Simple Notification Service
  "sqs", // Simple Queue Service
  "ssm",
  "states",
  "storagegateway",
  "transfer",
  "wisdom",
  "workspaces",
] as const;

export const AwsServices = uniq(RESOURCE_EXPLORER_SERVICES);

export type AwsService = (typeof AwsServices)[number];

export const isAwsService = (service: string): service is AwsService => {
  return AwsServices.includes(service as any);
};

export const AwsActionsShortcutServices = [
  "ec2",
  "eks",
  "s3",
  "sagemaker",
  "secretsmanager",
  "ssm",
  "rds-db",
] as const;

export const isAwsActionsShortcutService = (
  service: string
): service is AwsActionsShortcutService => {
  return AwsActionsShortcutServices.includes(service as any);
};

export const FRIENDLY_ACCESS_TYPES: Readonly<
  Record<keyof AwsAccesses, string>
> = {
  role: "role",
  policy: "policy",
  group: "group",
  "permission-set": "permission set",
  resource: "resource",
};

export const P0_TAG_NAME = "P0Security";
export const MANAGED_BY_P0 = "Managed by P0";
export const CREATED_BY_CLOUD_SHELL = "Created using CloudShell";
export const MANAGED_BY_TERRAFORM = "Managed by Terraform";

export const INLINE_ROLE_POLICY_CHAR_SIZE_LIMIT = 6144;
export const MANAGED_POLICY_CHAR_SIZE_LIMIT = 6144;
export const START_SSH_SESSION_DOCUMENT_NAME = "AWS-StartSSHSession";
/** Max amount of time to wait to provision the Linux OS user access */
export const PROVISION_USER_ACCESS_COMMAND_TIMEOUT_SECONDS = 60 * 10; // 10 minutes

/** RunCommand document that can
 *  - create a user
 *  - add or remove users to the sudoers file,
 *  - add or remove public keys from a users authorized_keys file
 */
export const PROVISION_USER_ACCESS_DOCUMENT_REFERENCE = {
  documentName: "P0ProvisionUserAccess",
  documentVersion: "$DEFAULT", // Use the default version as it is managed by us
} as const;

/** RunCommand document that retrieves machine SSH host keys information */
export const GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE = {
  documentName: "P0GetSshHostKeys",
  documentVersion: "$DEFAULT", // Use the default version as it is managed by us
} as const;

export const ENV_TO_P0_AWS_SUFFIX = {
  dev: "Dev",
  test: "Dev",
  prod: "",
  stage: "Stage",
} as const;

export const AWS_ACCOUNT_PATTERN = /^\d{12}$/;

export const GOVCLOUD_REGIONS = ["us-gov-east-1", "us-gov-west-1"];

export const P0_DEFAULT_AWS_REGION = "us-west-2";
export const P0_DEFAULT_GOVCLOUD_REGION = "us-gov-west-1";

/** use us-west-2 as default region, as IAM is global and any region should work. */
export const AWS_ROLE_URL = (context: {
  idcId: string;
  accountId: string;
  roleName: string;
}) =>
  `https://${context.idcId}.awsapps.com/start/#/console?account_id=${
    context.accountId
  }&destination=${encodeURIComponent(
    `https://us-west-2.console.aws.amazon.com/iam/home?region=us-west-2#/roles/details/${context.roleName}?section=permissions`
  )}`;

// TODO: change to switch the instance more principal types are added
export const awsUrlFor = (principalType: IdentityType) =>
  principalType === "service-account" ? AWS_ROLE_URL : undefined;

export const defaultRegion = (awsPartition: AwsPartition) => {
  switch (awsPartition) {
    case "aws-us-gov":
      return P0_DEFAULT_GOVCLOUD_REGION;
    case "aws":
      return P0_DEFAULT_AWS_REGION;
    default:
      throw assertNeverError(awsPartition);
  }
};
